// System Configuration — §9's eighth section. Founder/admin.
//
// "advanced config grouped together (runtime, memory/retrieval,
// governance/authority, evaluation/verification, security/secrets,
// diagnostics, experimental) rather than many top-level toggles."
//
// PRESERVES, from the v1 `/settings` page:
//   Audit Log   GET /api/audit-log
//               GET /api/audit-log/action-types
//
// THIS SECTION IS THE DECLARED HOME OF `/brain/memory`'s CONFIG HALF, AND THAT
// HALF DOES NOT EXIST
// ============================================================================
// §3.10 maps "`/brain/memory` (config half) → System Configuration". Read the
// component and that half is not there. `MemoryScreen.jsx` is 44 lines and
// every one of them is read-only: it renders the decision journal from
// `/journal` and Latest Thoughts derived from `/outcomes`, `/evaluations` and
// `/intelligence/observations`. There is no setting on that screen — no
// retention window, no retrieval depth, no embedding choice, nothing writable.
//
// The mapping document assumed a configuration half the component does not
// have. So this section is that half's declared destination, the destination
// exists, and it is empty **because there is nothing to move** — not because
// the move was skipped. The Memory *record* stays exactly where it is and keeps
// working (R2, R3); `/brain/memory` is not unlinked by this build.
//
// Nothing was invented to fill the gap. A retention-policy form that posts to
// no endpoint would satisfy the checklist item and misrepresent the system,
// which R6 exists to prevent.
import React, { useState } from "react";

import { ENDPOINTS } from "../api";
import { Block, Button, Gate, Row, Table, Value } from "../ui";
import useResource from "../useResource";

const PAGE = 25;

function MemoryConfiguration() {
  return (
    <Block
      title="Memory and retrieval"
      note="§3.10's declared home for the configuration half of /brain/memory."
    >
      <div className="state set-state" role="status">
        <strong>Nothing to configure.</strong>{" "}
        <span className="dim">
          The Memory screen is entirely read-only — a decision journal and derived thoughts, with
          no settings on it. This section is where memory configuration would live if it existed;
          it is empty because there is none, not because it was left out. The Memory screen itself
          is unchanged and still reachable at <code className="mono">/brain/memory</code>.
        </span>
      </div>
    </Block>
  );
}

function AuditLog() {
  const [actionType, setActionType] = useState("");
  const [page, setPage] = useState(0);

  const types = useResource(ENDPOINTS.auditActionTypes);
  const log = useResource(ENDPOINTS.auditLog, {
    params: {
      limit: PAGE,
      offset: page * PAGE,
      ...(actionType ? { action_type: actionType } : {}),
    },
    isEmpty: (d) => !(d?.entries || d?.logs || []).length,
  });

  const rows = log.data?.entries || log.data?.logs || [];
  const total = log.data?.total;

  return (
    <Block
      title="Audit log"
      note="Every governed action, who took it, and when. Preserved from the v1 Settings page."
      actions={
        <label className="set-inline set-filter">
          <span className="set-filter-label dim">Action</span>
          <select
            className="set-input"
            aria-label="Filter by action type"
            value={actionType}
            onChange={(e) => {
              setActionType(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All actions</option>
            {(types.data?.action_types || []).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      }
    >
      <Gate resource={log} empty="No audit entries match this filter.">
        {() => (
          <>
            <Table head={["When", "Action", "Operator", "Description"]}>
              {rows.map((e, i) => (
                <tr key={e.audit_id || e.id || `${e.timestamp}-${i}`}>
                  <td className="mono dim">{e.timestamp || e.created_at || "—"}</td>
                  <td className="mono">{e.action_type || "—"}</td>
                  <td>{e.operator_name || e.operator_id || "—"}</td>
                  <td className="dim">{e.description || "—"}</td>
                </tr>
              ))}
            </Table>
            <div className="set-pager">
              <Button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} kind="quiet">
                Previous
              </Button>
              <span className="dim">
                {page * PAGE + 1}–{page * PAGE + rows.length}
                {typeof total === "number" ? ` of ${total}` : ""}
              </span>
              <Button
                onClick={() => setPage((p) => p + 1)}
                disabled={rows.length < PAGE}
                kind="quiet"
              >
                Next
              </Button>
            </div>
          </>
        )}
      </Gate>
    </Block>
  );
}

/**
 * §9's remaining groupings.
 *
 * §9 asks for advanced configuration "grouped together … rather than many
 * top-level toggles", and names seven groups. Two of them have something behind
 * them tonight: governance/authority is the audit log above, and
 * memory/retrieval is the empty block above that. The other five have no
 * configuration endpoint at all.
 *
 * They are named rather than omitted, because §9's instruction is about
 * *grouping*, and a group that silently disappears when it is empty teaches the
 * reader that the grouping is smaller than it is.
 */
function Groupings() {
  const GROUPS = [
    ["Runtime", "No runtime configuration endpoint exists."],
    ["Evaluation and verification", "Critic configuration is a JSON file in the repository, not an endpoint."],
    ["Security and secrets", "Secrets are environment variables. No endpoint exposes or sets them, deliberately."],
    ["Diagnostics", "Integration, scheduler and cost diagnostics are in Infrastructure."],
    ["Experimental", "Feature flags are environment variables. No endpoint toggles them."],
  ];

  return (
    <Block
      title="Other configuration groups"
      note="§9's remaining groupings, and the honest state of each."
    >
      {GROUPS.map(([label, detail]) => (
        <Row key={label} label={label}>
          <Value>
            <span className="dim">{detail}</span>
          </Value>
        </Row>
      ))}
    </Block>
  );
}

export default function SystemConfiguration() {
  return (
    <>
      <MemoryConfiguration />
      <AuditLog />
      <Groupings />
    </>
  );
}
